import { devWarn } from "../../utils/devWarning";
import type { Disposable } from "./disposable";
import { TableChildRow, type TableRow, type TableRowBase } from "./TableRow";
import type { DataTableSortState } from "./types";

export type DataTableLoadState = "idle" | "loading" | "loaded" | "failed";

export type DataTablePaginationMode = "page" | "append";

/**
 * page 모드에서 미리 받을 page의 범위. `cacheSignature()`가 있는 source에만 적용된다.
 * - `"neighbors"`: page를 표시할 때마다 앞뒤 page를 받는다.
 * - `"paging"`: 같은 조건에서 `goToPage`로 page를 넘겼을 때만, 넘긴 방향의 다음 page 하나를 받는다.
 */
export type DataTablePrefetchMode = "neighbors" | "paging";

export type DataTableAppendReq = {
  cursor: string | null;
  size: number;
  sort: DataTableSortState | null;
  signal: AbortSignal;
};

export type DataTableAppendResp<Row extends TableRow> = {
  rows: readonly Row[];
  /** null은 목록 끝. cursor 변환과 endpoint 선택은 소비처 책임이다. */
  nextCursor: string | null;
  total?: number;
};

export type DataTableAppendState = {
  hasMore: boolean;
  loadingMore: boolean;
  errorMessage: string | null;
  /** 조회되지 않은 전체 개수는 null이며 로드된 개수로 추측하지 않는다. */
  total: number | null;
  /** refresh/context/정렬 교체 경계. append와 selection 변경에는 유지된다. */
  generation: number;
};

export type DataTablePageReq = {
  page: number;
  size: number;
};

/** `onPage`의 두 번째 인자. source가 호출마다 채운다. */
export type DataTablePageOptions = {
  /**
   * source가 이 요청을 버리면 abort된다. 전송에 넘기면 버린 요청이 취소된다.
   * 넘기지 않아도 늦은 응답은 source가 버린다.
   */
  signal: AbortSignal;
};

export type DataTablePageResp<Row extends TableRow> = {
  rows: readonly Row[];
  total: number;
  page: number;
  size: number;
};

export type DataTableSourceSnapshot<Row extends TableRow> = {
  // 현재 페이지 또는 누적 결과의 visible 행. 숨김 행은 source가 rawRows에 보관한다.
  rows: readonly Row[];
  loadState: DataTableLoadState;
  errorMessage: string | null;
  page: number;
  size: number;
  // page 모드의 서버 total. append의 전체 수는 append.total을 읽는다(unknown은 null).
  // 숨김 행을 **포함**한다(visible 전역 카운트는 전 페이지 fetch 없이
  // 알 수 없다). 페이지 수·이동은 이 값 기준이 맞고, "Showing X of Y" 오차는 수용한다.
  total: number;
  // 선택된 행 id 집합. 뷰(DataTable/DataGridView)가 체크 상태를 이 값으로 판정한다.
  selectedIds: ReadonlySet<string>;
  /** append 모드에서만 존재. 전체 결과 수는 이 객체의 total을 사용한다. */
  append?: DataTableAppendState;
};

export type DataTableSourceOptions<Row extends TableRow = TableRow> = {
  paginationMode?: DataTablePaginationMode;
  /** 기본 `"neighbors"`. */
  prefetchMode?: DataTablePrefetchMode;
  initialPage?: number;
  initialPageSize?: number;
  resolveErrorMessage?: (error: unknown) => string;
  // 선택 변경 통지. 토글마다 현재 선택된 행 객체 전체를 넘긴다(선택은 페이지를 넘어가도 유지).
  // 최상위 행과 하위 행은 의미가 섞이지 않게 따로 넘긴다(getSelectedRows/getSelectedChildRows).
  onSelectionChange?: (rows: readonly Row[], childRows: readonly TableChildRow[]) => void;
};

const defaultInitialPage = 1;
const defaultInitialPageSize = 20;
// prefetch 캐시 상한. 현재 페이지와 이웃 몇 개만 유지해 메모리 사용을 묶는다.
const maxCachedPages = 8;

type PendingPage<Row extends TableRow> = {
  promise: Promise<DataTablePageResp<Row>>;
  controller: AbortController;
  // pendingPages의 키. 서명이 없는 source의 요청은 등록하지 않으므로 null이다.
  cacheKey: string | null;
  // 캐시 키에서 page를 뺀 부분(서명 + size). 조건이 달라진 요청을 가려낸다. 서명이 없으면 null.
  scope: string | null;
  // 이 응답을 캐시하려고 기다리는 prefetch가 있다. 화면이 다른 page로 가도 취소하지 않는다.
  prefetch: boolean;
};

type DisplayedPage = { scope: string; page: number };

function toCacheKey(scope: string, page: number): string {
  return `${scope}|p=${page}`;
}

export abstract class DataTableSource<Row extends TableRow> implements Disposable {
  readonly paginationMode: DataTablePaginationMode;
  private readonly prefetchMode: DataTablePrefetchMode;
  private appendController: AbortController | null = null;
  private nextCursor: string | null = null;
  private readonly consumedCursors = new Set<string>();
  private readonly listeners = new Set<() => void>();
  private readonly resolveErrorMessage: (error: unknown) => string;
  private snapshot: DataTableSourceSnapshot<Row>;
  private requestSerial = 0;
  private disposed = false;
  // 페이지 응답 캐시(prefetch 포함). 키는 cacheSignature + size + page. 캐시 비활성 source는 비어 있다.
  private readonly pageCache = new Map<string, DataTablePageResp<Row>>();
  // 캐시를 비울 때마다 증가. 진행 중이던 load/prefetch가 무효화 이전 응답을 다시 쓰지 못하게 막는다.
  private cacheGeneration = 0;
  // 진행 중인 page 요청(prefetch 포함). 키는 pageCache와 같다. 같은 키의 요청을 한 번만 보내려고 둔다.
  // 서명이 없는 source는 키가 없어 등록하지 않는다.
  private readonly pendingPages = new Map<string, PendingPage<Row>>();
  // 화면에 반영할 요청. 다른 요청으로 바뀌면 취소한다.
  private displayRequest: PendingPage<Row> | null = null;
  // 직전에 표시한 page. prefetchMode "paging"이 같은 조건 안의 이동 방향을 판정한다.
  private displayedPage: DisplayedPage | null = null;
  // 현재 페이지의 원본 행(canShow 숨김 포함). snapshot.rows에는 visible만 싣는다 —
  // 필터는 스냅샷 경계(visibleRows) 한 곳에서만 일어나고, mutation은 이 배열 기준이다.
  private rawRows: readonly Row[] = [];
  // rawRows를 id로 조회하는 인덱스(선언된 하위 행 포함). 선택 해석·detach 판정이 공유한다.
  private rowIndexCache: {
    rawRows: readonly Row[];
    index: ReadonlyMap<string, TableRowBase>;
  } | null = null;
  // 선택 상태(표/그리드 공용). **키가 멤버십**이고, 값은 선택 시점에 받은 행 객체 캡처다 —
  // 현재 페이지에 없는 선택(cross-page)을 돌려줄 때만 쓰인다. 페이지 안 행은 읽을 때
  // rawRows에서 해석하므로 이 캡처를 커밋마다 최신화하지 않는다(PIT-stored-object-identity-drift).
  // 최상위 행과 하위 행(TableChildRow)이 한 저장소를 쓴다 — id 공간을 공유하므로 소비처는
  // 두 종류의 id가 충돌하지 않게 만든다.
  private readonly selectedRows = new Map<string, TableRowBase>();
  // 스냅샷에 실을 선택 id 집합. selectedRows가 바뀔 때만 재계산해 불변 참조로 노출한다.
  private selectionIds: ReadonlySet<string> = new Set();
  private readonly onSelectionChange?: (
    rows: readonly Row[],
    childRows: readonly TableChildRow[],
  ) => void;

  protected constructor({
    paginationMode = "page",
    prefetchMode = "neighbors",
    initialPage = defaultInitialPage,
    initialPageSize = defaultInitialPageSize,
    resolveErrorMessage = resolveDefaultErrorMessage,
    onSelectionChange,
  }: DataTableSourceOptions<Row> = {}) {
    this.paginationMode = paginationMode;
    this.prefetchMode = prefetchMode;
    this.resolveErrorMessage = resolveErrorMessage;
    this.onSelectionChange = onSelectionChange;
    this.snapshot = {
      rows: [],
      loadState: "idle",
      errorMessage: null,
      page: initialPage,
      size: initialPageSize,
      total: 0,
      selectedIds: this.selectionIds,
      ...(paginationMode === "append"
        ? {
            append: {
              hasMore: false,
              loadingMore: false,
              errorMessage: null,
              total: null,
              generation: 0,
            },
          }
        : {}),
    };
  }

  // 구독/스냅샷: useSyncExternalStore 계약 (subscribe 재호출은 dispose 후 재활성 진입점)
  getSnapshot = (): DataTableSourceSnapshot<Row> => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    // 재구독은 dispose 이후 같은 source를 다시 활성화하는 진입점이다.
    this.disposed = false;
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 정책 hook: 소비처가 override (행 재정렬 허용·서버 정렬 여부·활성 정렬 통지)
  get canReorderRows(): boolean {
    return false;
  }

  /**
   * source가 정렬을 서버(또는 자체)에서 수행하면 true로 override 한다. 그러면 DataTable은
   * 클라이언트 정렬을 건너뛰고 source가 돌려준 행 순서를 그대로 쓰며, 정렬 변경 시 setSort를 호출한다.
   * 기본 false → DataTable이 TableRow.compare로 로드된 행을 클라이언트 정렬한다.
   */
  get sortsOnServer(): boolean {
    return false;
  }

  // 서버 정렬 source가 onPage 쿼리/cacheSignature에서 참조할 활성 정렬. 기본 source는 쓰지 않는다.
  protected sortState: DataTableSortState | null = null;

  /**
   * 활성 정렬 변경 통지. append + sortsOnServer일 때만 변경 시 첫 묶음부터 다시 받는다.
   * 서버 페이징 source는 override해 super.setSort(sort) 후 캐시를 비우고 첫 페이지부터 다시 받으며,
   * onPage 쿼리와 cacheSignature에 sortState를 반영한다. 서버 정렬 append는 super가 reset을 소유하므로
   * override에서 refresh를 중복 호출하지 않는다. null은 정렬 해제를 뜻한다.
   */
  setSort(sort: DataTableSortState | null): void {
    const changed =
      this.sortState?.columnKey !== sort?.columnKey ||
      this.sortState?.direction !== sort?.direction;
    this.sortState = sort;
    if (
      changed &&
      this.sortsOnServer &&
      this.paginationMode === "append" &&
      this.snapshot.loadState !== "idle"
    ) {
      void this.refresh();
    }
  }

  // 라이프사이클: 첫 로드·dispose·refresh
  initialize(): Promise<void> {
    // page init 시 현재 snapshot의 page/size 기준으로 첫 데이터를 로드한다.
    return this.refresh();
  }

  dispose(): void {
    this.appendController?.abort();
    this.appendController = null;
    this.consumedCursors.clear();
    // page dispose 시 구독자와 진행 중인 비동기 page 요청을 함께 무효화하고 취소한다.
    this.disposed = true;
    this.requestSerial += 1;
    this.displayRequest?.controller.abort();
    this.displayRequest = null;
    this.clearPageCache();
    this.displayedPage = null;
    // 재활성(subscribe) 시 이전 페이지 원본이 남아 있지 않게 비운다. 인덱스도 함께 버린다 —
    // 안 버리면 캐시가 방금 놓아준 행 배열을 계속 붙잡는다.
    this.rawRows = [];
    this.rowIndexCache = null;
    this.selectedRows.clear();
    this.selectionIds = new Set();
    this.listeners.clear();
  }

  /**
   * 데이터가 바뀐 뒤 호출한다. prefetch 캐시를 비우고 page를 다시 받는다. page를 생략하면 현재
   * 페이지다. 데이터가 바뀐 뒤 다른 page로 갈 때는 goToPage가 아니라 이 메서드에 page를 넘긴다.
   * goToPage는 캐시에 있는 page를 요청 없이 표시한다. append 모드는 page를 쓰지 않는다.
   */
  refresh(page: number = this.snapshot.page): Promise<void> {
    if (this.paginationMode === "append") return this.resetAppend(this.snapshot.size);
    this.clearPageCache();
    return this.loadPage(page, this.snapshot.size, false);
  }

  // 페이징: 캐시 히트면 즉시 표시 + 이웃 prefetch, 아니면 로드
  goToPage(page: number): Promise<void> {
    if (this.paginationMode === "append") {
      devWarn("[c6] goToPage is unavailable in append mode; use loadMore or refresh.");
      return Promise.resolve();
    }
    const size = this.snapshot.size;
    const cacheKey = this.buildCacheKey(page, size);
    if (cacheKey !== null) {
      const cached = this.pageCache.get(cacheKey);
      if (cached !== undefined) {
        // 캐시 히트: 진행 중 로드를 무효화하고 즉시 표시한 뒤 prefetchMode에 따라 미리 받는다.
        this.requestSerial += 1;
        this.abortPendingOutsideScope(this.buildCacheScope(size));
        const from = this.displayedPage;
        this.replaceLoadedPage(cached);
        this.schedulePrefetch(from, page, size);
        // prefetch 뒤에 바꾼다. 앞의 화면용 요청이 새 prefetch 대상이면 취소하지 않고 그 요청을 쓴다.
        this.replaceDisplayRequest(null);
        return Promise.resolve();
      }
    }
    return this.loadPage(page, size, true);
  }

  setPageSize(size: number): Promise<void> {
    if (this.paginationMode === "append") return this.resetAppend(size);
    return this.loadPage(defaultInitialPage, size, false);
  }

  /** 추가 로딩과 명시적 재시도. viewport/keyboard/여러 뷰 요청을 source에서 중복 억제한다. */
  loadMore(): Promise<void> {
    const append = this.snapshot.append;
    if (
      this.disposed ||
      !append ||
      !append.hasMore ||
      append.loadingMore ||
      this.snapshot.loadState !== "loaded"
    )
      return Promise.resolve();
    return this.loadAppend(false);
  }

  private resetAppend(size: number): Promise<void> {
    this.appendController?.abort();
    this.disposed = false;
    this.requestSerial += 1;
    this.clearPageCache();
    this.nextCursor = null;
    this.consumedCursors.clear();
    const selectionChanged = this.selectedRows.size > 0;
    this.selectedRows.clear();
    this.selectionIds = new Set();
    this.commitRows([], {
      loadState: "loading",
      errorMessage: null,
      page: 1,
      size,
      total: 0,
      selectedIds: this.selectionIds,
      append: {
        hasMore: false,
        loadingMore: false,
        errorMessage: null,
        total: null,
        generation: (this.snapshot.append?.generation ?? 0) + 1,
      },
    });
    if (selectionChanged) this.onSelectionChange?.([], []);
    return this.loadAppend(true);
  }

  private async loadAppend(initial: boolean): Promise<void> {
    const requestId = ++this.requestSerial;
    const controller = new AbortController();
    this.appendController = controller;
    const cursor = this.nextCursor;
    const { size } = this.snapshot;
    this.setSnapshot({
      ...this.snapshot,
      append: { ...this.snapshot.append!, loadingMore: !initial, errorMessage: null },
    });
    try {
      const response = await this.onAppend({
        cursor,
        size,
        sort: this.sortState,
        signal: controller.signal,
      });
      if (this.disposed || requestId !== this.requestSerial) return;
      if (
        response.nextCursor !== null &&
        (response.nextCursor === cursor || this.consumedCursors.has(response.nextCursor))
      ) {
        throw new Error("The next cursor must advance. Retry after correcting the data source.");
      }
      if (cursor !== null) this.consumedCursors.add(cursor);
      this.nextCursor = response.nextCursor;
      // 중복 id는 기존 위치·객체를 보존한다. append가 로컬 mutation/선택을 되돌리지 않는다.
      const ids = new Set(this.rawRows.map((row) => row.getRowId()));
      const added = response.rows.filter((row) => {
        const id = row.getRowId();
        if (ids.has(id)) return false;
        ids.add(id);
        return true;
      });
      const rows = [...this.rawRows, ...added];
      const total = response.total ?? this.snapshot.append!.total;
      this.commitRows(rows, {
        loadState: "loaded",
        errorMessage: null,
        page: initial ? 1 : this.snapshot.page + 1,
        total: total ?? rows.length,
        append: {
          ...this.snapshot.append!,
          total,
          hasMore: response.nextCursor !== null,
          loadingMore: false,
          errorMessage: null,
        },
      });
    } catch (error) {
      if (this.disposed || requestId !== this.requestSerial) return;
      const message = this.resolveErrorMessage(error);
      this.setSnapshot({
        ...this.snapshot,
        loadState: initial ? "failed" : "loaded",
        errorMessage: initial ? message : null,
        append: {
          ...this.snapshot.append!,
          loadingMore: false,
          errorMessage: initial ? null : message,
        },
      });
    } finally {
      if (this.appendController === controller) this.appendController = null;
    }
  }

  // mutation: 낙관적 snapshot 갱신 + 캐시 무효화(total/페이지 경계 변동)
  replaceRow(row: Row): void {
    // 행 데이터가 바뀌면 같은 signature로 캐시된 다른 페이지도 신선하지 않다.
    this.clearPageCache();
    // 캡처는 그 행의 최신값을 **확실히 아는 순간**에 갱신한다 — 선택 시점과 여기(호출자가 건넨
    // 행). 페이지 안 행은 어차피 rawRows에서 해석되지만, 페이지 밖 행의 교체는 거기 안 실려
    // 캡처만이 그 값을 가진다. id는 그대로라 selectedIds 재계산·통지는 불필요.
    if (this.selectedRows.has(row.getRowId())) {
      this.selectedRows.set(row.getRowId(), row);
    }
    // 교체는 rawRows 기준 — 숨겨진 행도 교체되고, 교체로 canShow가 바뀌면
    // 재필터로 행이 자연히 사라지거나 재등장한다.
    this.commitRows(
      this.rawRows.map((currentRow) =>
        currentRow.getRowId() === row.getRowId() ? row : currentRow,
      ),
    );
  }

  removeRow(rowId: string): void {
    // 제거·판정은 rawRows 기준 — 숨겨진 행의 삭제도 서버에선 줄어든 것이므로 total이 줄어야 한다.
    const nextRawRows = this.rawRows.filter((row) => row.getRowId() !== rowId);
    const removedFromPage = nextRawRows.length !== this.rawRows.length;

    // 제거된 행은 선택에서도 빠진다(현재 페이지 밖에서 선택돼 있던 경우 포함).
    let removedFromSelection = this.selectedRows.delete(rowId);
    // 그 행을 부모로 가리키는 하위 행 선택도 함께 거둔다 — 부모가 사라지면 자식은 어느 화면에도
    // 안 나오므로, 선택만 남으면 사용자가 해제할 수 없는 고아가 된다. 판정을 **부모 링크**로
    // 하므로 페이지 밖 부모를 지울 때도 성립한다(rawRows에서 못 찾아 자식이 남던 갭).
    for (const [selectedId, selectedRow] of this.selectedRows) {
      if (selectedRow instanceof TableChildRow && selectedRow.getParentRowId() === rowId) {
        if (this.selectedRows.delete(selectedId)) {
          removedFromSelection = true;
        }
      }
    }
    if (!removedFromPage && !removedFromSelection) {
      return;
    }

    if (removedFromSelection) {
      this.selectionIds = new Set(this.selectedRows.keys());
    }
    if (removedFromPage) {
      // 페이지에서 빠지면 total과 페이지 경계가 바뀌므로 prefetch 캐시를 비운다.
      this.clearPageCache();
      this.commitRows(nextRawRows, {
        total: Math.max(0, this.snapshot.total - 1),
        selectedIds: this.selectionIds,
      });
    } else {
      // 선택에서만 빠진 경우 — rawRows 무변경이므로 행 배열은 그대로 두고 선택만 갱신한다.
      this.setSnapshot({ ...this.snapshot, selectedIds: this.selectionIds });
    }
    if (removedFromSelection) {
      this.onSelectionChange?.(this.getSelectedRows(), this.getSelectedChildRows());
    }
  }

  upsertRow(row: Row): void {
    // 행 추가/갱신은 total과 페이지 경계를 바꿀 수 있으므로 prefetch 캐시를 비운다.
    this.clearPageCache();
    const rowId = row.getRowId();

    // 캡처 갱신(replaceRow와 동일 이유). id는 그대로라 selectedIds 재계산·통지는 불필요 —
    // 선택 멤버십이 아니라 데이터 갱신이기 때문.
    if (this.selectedRows.has(rowId)) {
      this.selectedRows.set(rowId, row);
    }
    // 탐색·삽입은 rawRows 기준 — 숨겨진 기존 행의 upsert가 신규 삽입으로 오판되면
    // 중복 삽입 + total 이중 증가가 된다.
    const existingIndex = this.rawRows.findIndex((currentRow) => currentRow.getRowId() === rowId);
    if (existingIndex < 0) {
      // 신규 행이 canShow()=false면 total+1인데 화면은 무변화 — 정상이다(서버에는 늘었다).
      this.commitRows([row, ...this.rawRows], { total: this.snapshot.total + 1 });
      return;
    }

    const nextRawRows = Array.from(this.rawRows);
    nextRawRows[existingIndex] = row;
    this.commitRows(nextRawRows);
  }

  reorderRows(rows: readonly Row[]): void {
    if (!this.canReorderRows) {
      return;
    }
    // 뷰는 visible만 안다 — 숨김 행이 있으면 rawRows 재구성 시 숨김 행의 위치가 미정의라
    // 재정렬을 받지 않는다(canShow 숨김 × canReorderRows 비호환 계약 — RFC-data-table).
    if (this.rawRows.some((row) => !row.canShow())) {
      devWarn(
        "[c6] DataTableSource.reorderRows is not supported while canShow() hides rows on the page; ignoring.",
      );
      return;
    }
    this.commitRows(Array.from(rows));
  }

  /**
   * 선택된 id를 행 객체로 **해석(resolve)** 한다 — 계산이 아니라 두 곳에서의 조회이고,
   * **현재 페이지 사본이 선택 시점 캡처를 이긴다**. 행은 불변 값 객체라 커밋마다 새 인스턴스로
   * 흘러들므로, 저장해 둔 객체를 그대로 돌려주면 화면과 어긋난다(PIT-stored-object-identity-drift). 뷰가 매 렌더
   * snapshot에서 다시 읽는 것과 같은 규율이다.
   *
   * 조회 범위는 `rawRows` — `canShow()=false`로 숨긴 행/자식도 여기 있으므로 최신 인스턴스로
   * 해석된다. 현재 페이지에 없는 선택(cross-page)만 캡처가 답한다.
   *
   * 캡처는 모든 선택 id를 덮으므로(멤버십의 진실 = 이 Map의 키) 해석은 실패하지 않는다.
   * 순서도 이 Map의 삽입 순서 = 선택한 순서 그대로다.
   */
  private resolveSelection(): readonly TableRowBase[] {
    const rowIndex = this.currentRowIndex();
    return Array.from(
      this.selectedRows,
      ([rowId, capturedRow]) => rowIndex.get(rowId) ?? capturedRow,
    );
  }

  // 선택: 멤버십은 소스가 소유하고, 뷰는 showCheckbox/rowSelection이 노출한 상태를 snapshot
  // selectedIds로 그린다.
  // 선택은 페이지를 넘어가도 유지되고(cross-page), getSelectedRows가 그 행들을 돌려준다.
  // 최상위 행만 돌려준다 — 하위 행은 getSelectedChildRows()가 따로 돌려줘 의미가 섞이지 않는다.
  getSelectedRows(): readonly Row[] {
    // 선택 API 타입이 Row | TableChildRow로 제한돼 있어 아닌 쪽을 걸러내면 Row다.
    return this.resolveSelection().filter((row): row is Row => !(row instanceof TableChildRow));
  }

  // 선택된 하위 행(TableChildRow). 부모 집계 토글·하위 행 체크박스로 담긴 것들이다.
  getSelectedChildRows(): readonly TableChildRow[] {
    return this.resolveSelection().filter(
      (row): row is TableChildRow => row instanceof TableChildRow,
    );
  }

  // 선택 총량 — 최상위 행 + 하위 행. canSelectMore 같은 수용량 판정의 기준값이다.
  get selectedRowCount(): number {
    return this.selectedRows.size;
  }

  isRowSelected(rowId: string): boolean {
    return this.selectedRows.has(rowId);
  }

  /**
   * Shift 범위의 전체 top-level 행 목록. null은 뷰의 현재 표시 순서를 사용한다.
   * 페이지 간 범위를 지원하는 source는 페이지를 자르기 전 필터·정렬 결과를 반환한다.
   * 입력 시점의 최신 객체를 동기적으로 제공하며, 조회나 선택 변경을 수행하지 않는다.
   * 전체 결과를 모르는 서버 source는 기본 null을 유지한다(부분 캐시를 전체로 간주하지 않는다).
   */
  getSelectionRangeRows(): readonly Row[] | null {
    return null;
  }

  /**
   * 새 행을 더 담을 수 있는가(선택 상한 seam). 기본 무제한.
   *
   * **사전 predicate** — 선택 전에 묻는다. 행 무관 수용량(최대 선택 수 등)만 답하고,
   * 행 고유 자격은 TableRow.canSelect()가 답한다. 이미 선택된 행에는 묻지 않으므로
   * (해제는 항상 가능), 현재 선택 수에 의존하는 상한을 그대로 표현할 수 있다 —
   * 상한에 닿으면 남은 행이 canSelectRow에서 false 로 바뀐다.
   */
  protected canSelectMore(): boolean {
    return true;
  }

  /**
   * 이 행을 **지금** 새로 선택할 수 있는가 = row.canSelect() && canSelectMore().
   *
   * 합성 지점이다 — **override 하지 말 것**. 규칙은 row.canSelect()(행 고유 자격)와
   * canSelectMore()(행 무관 상한)에만 쓴다. (TS에 final이 없으므로 이 계약은
   * 이 주석 + RFC-data-table으로 강제한다.)
   *
   * 뷰(DataTable/DataGridView)는 이 값으로 체크박스를 **disable** 하고 헤더 전체선택의
   * 대상·상태를 정한다. 소스가 조용히 거절하는 대신 규칙이 화면에 보이게 하려는 것이다.
   *
   * Args:
   *   row: 판정할 행.
   *
   * Returns:
   *   선택 가능하면 true.
   */
  canSelectRow(row: TableRowBase): boolean {
    return row.canSelect() && this.canSelectMore();
  }

  toggleRowSelection(row: Row | TableChildRow): void {
    this.setRowSelected(row, !this.selectedRows.has(row.getRowId()));
  }

  setRowSelected(row: Row | TableChildRow, selected: boolean): void {
    const rowId = row.getRowId();
    if (selected) {
      // 이미 선택된 행은 다시 묻지 않는다 — 상한형 규칙이 자기 자신을 거절하게 된다.
      if (!this.selectedRows.has(rowId) && !this.canSelectRow(row)) {
        return;
      }
      // 같은 참조면 무변경. 다른 객체(재로드 등)면 최신으로 교체한다.
      if (this.selectedRows.get(rowId) === row) {
        return;
      }
      this.selectedRows.set(rowId, row);
    } else if (!this.selectedRows.delete(rowId)) {
      return;
    }
    this.commitSelection();
  }

  // 헤더 전체선택 등 여러 행을 한 번에 켜고/끈다(현재 페이지 행 묶음 + 노출된 하위 행).
  // 실제 변경이 있을 때만 통지. canSelectRow 는 행마다 다시 묻는다 — 상한형 규칙이 선택이
  // 차오르는 중에도 성립한다.
  setRowsSelected(rows: readonly (Row | TableChildRow)[], selected: boolean): void {
    let changed = false;
    for (const row of rows) {
      const rowId = row.getRowId();
      if (selected) {
        if (!this.selectedRows.has(rowId) && !this.canSelectRow(row)) {
          continue;
        }
        if (this.selectedRows.get(rowId) !== row) {
          this.selectedRows.set(rowId, row);
          changed = true;
        }
      } else if (this.selectedRows.delete(rowId)) {
        changed = true;
      }
    }
    if (changed) {
      this.commitSelection();
    }
  }

  // 일반 클릭·single selection처럼 현재 선택을 한 묶음으로 **교체**한다. clearSelection 뒤
  // setRowsSelected를 연달아 부르면 소비처가 빈 중간 상태와 최종 상태를 두 번 관찰하므로,
  // 교체는 내부 map에서 완성한 뒤 한 번만 commit한다. canSelectMore는 비운 map에 새 묶음이
  // 차오르는 과정에서 평가돼 기존 선택 상한 때문에 교체 대상이 부당하게 거절되지 않는다.
  replaceSelection(rows: readonly (Row | TableChildRow)[]): void {
    const previousEntries = Array.from(this.selectedRows.entries());
    const previouslySelectedIds = new Set(previousEntries.map(([rowId]) => rowId));
    // 중복 id는 cardinality를 늘리지 않고 최신 객체만 캡처한다. Map은 최초 삽입 위치를
    // 유지하므로 최종 selection 순서도 요청 rows의 첫 등장 순서를 따른다.
    const requestedRows = new Map<string, Row | TableChildRow>();
    for (const row of rows) {
      requestedRows.set(row.getRowId(), row);
    }

    this.selectedRows.clear();

    // 요청에 남아 있는 기존 멤버십은 자격을 다시 묻지 않는다. setRowSelected와 같은 규칙이라,
    // replaceRow 뒤 최신 행이 canSelect=false가 됐어도 replace 입력이 그 선택을 지우지 않는다.
    // 먼저 예약해야 뒤에 등장하는 기존 id도 canSelectMore 수용량에 포함된다.
    for (const [rowId, row] of requestedRows) {
      if (previouslySelectedIds.has(rowId)) {
        this.selectedRows.set(rowId, row);
      }
    }

    // 신규 id에만 현재 행 자격과, 예약된 기존 멤버십을 포함한 새 묶음의 상한을 적용한다.
    for (const [rowId, row] of requestedRows) {
      if (previouslySelectedIds.has(rowId)) continue;
      if (this.canSelectRow(row)) {
        this.selectedRows.set(rowId, row);
      }
    }

    // 위 두 단계는 자격 검증을 위해 기존 id를 먼저 넣었다. 외부에 보이는 선택 순서는 다시
    // 요청 순서로 정렬하되, 검증을 통과한 멤버십과 최신 객체는 그대로 보존한다.
    const selectedRowIds = new Set(this.selectedRows.keys());
    this.selectedRows.clear();
    for (const [rowId, row] of requestedRows) {
      if (selectedRowIds.has(rowId)) {
        this.selectedRows.set(rowId, row);
      }
    }

    const nextEntries = Array.from(this.selectedRows.entries());
    const changed =
      previousEntries.length !== nextEntries.length ||
      previousEntries.some(
        ([previousId, previousRow], index) =>
          nextEntries[index]?.[0] !== previousId || nextEntries[index]?.[1] !== previousRow,
      );
    if (changed) {
      this.commitSelection();
    }
  }

  clearSelection(): void {
    if (this.selectedRows.size === 0) {
      return;
    }
    this.selectedRows.clear();
    this.commitSelection();
  }

  private commitSelection(): void {
    this.selectionIds = new Set(this.selectedRows.keys());
    this.setSnapshot({ ...this.snapshot, selectedIds: this.selectionIds });
    this.onSelectionChange?.(this.getSelectedRows(), this.getSelectedChildRows());
  }

  /**
   * 현재 페이지가 담고 있는 모든 행을 id로 찾을 수 있는 인덱스 — 최상위 행 + 각 행이 **선언한**
   * 하위 행. 선택 해석과 detach 판정이 이 하나를 공유한다. rawRows 참조가 바뀔 때만 다시 만든다.
   */
  private currentRowIndex(): ReadonlyMap<string, TableRowBase> {
    if (this.rowIndexCache?.rawRows === this.rawRows) {
      return this.rowIndexCache.index;
    }

    const index = new Map<string, TableRowBase>();
    for (const row of this.rawRows) {
      const rowId = row.getRowId();
      index.set(rowId, row);
      for (const childRow of row.getRowChildren()) {
        if (childRow.getParentRowId() !== rowId) {
          // 링크가 어긋나면 그 자식은 detach 판정에서 엉뚱한 부모를 본다 — 조용히 틀리는 대신 알린다.
          devWarn(
            `[c6] TableChildRow "${childRow.getRowId()}" declares parent "${childRow.getParentRowId()}" but is listed by row "${rowId}".`,
          );
        }
        index.set(childRow.getRowId(), childRow);
      }
    }
    this.rowIndexCache = { rawRows: this.rawRows, index };
    return index;
  }

  /**
   * 커밋된 묶음이 **반증한** 선택을 거둔다. 두 가지가 반증이다.
   *
   * **① 사라진 하위 행**: 부모가 사라지면 자식이 어느 체크박스에도 안 붙어 사용자가 해제할 수
   * 없는 고아가 되는 것과 같은 경로다(`removeRow`와 짝). 판정은 두 축으로 나뉜다 — **부모
   * 링크**는 "판정할 자격이 있는가"(내 부모가 이번 묶음에 있어야 그 선언을 근거로 생사를 말할
   * 수 있다. 부모가 다른 페이지면 근거가 없으므로 cross-page로 유지), **페이지 전체 인덱스**는
   * "아직 있는가"(어느 행도 선언하지 않을 때만 사라진 것이다).
   *
   * **② 소속이 바뀐 행(소속 변경 가드)**: 선택된 행이 캡처(선택 시점)와 **다른 소속**으로
   * 해석되면 — 자식의 부모 변경, top-level↔child 이동 포함 — 그 선택을 거두고 devWarn 한다.
   * 수거·`removeRow` 판정의 근거가 캡처의 부모 링크인데 커밋은 캡처를 손대지 않으므로, 소속이
   * 바뀐 선택을 유지하면 이후 판정이 옛 소속으로 오판한다(옛 부모 removeRow가 새 부모 아래
   * 자식을 잘못 거두거나, 사라진 자식이 영구 잔존한다). 실사용 시나리오가 없어 지원 대신
   * 오남용을 차단한다. 캡처가 옛 소속을 물고 있는 것이 곧 감지 증거다(RFC-data-table §15).
   * 소속을 옮기려는 소비처는 커밋 후 선택을 다시 담는다. 선택 안 된 행의 소속 변경은 캡처가 없어 무해.
   *
   * `canShow()=false`는 수거 대상이 아니다 — 선언이 살아 있으면 숨김일 뿐이고, 숨김은
   * "다른 페이지에 있음"과 동급으로 선택을 유지한다(RFC-data-table §숨김 × 선택).
   *
   * Returns:
   *   거둔 것이 있으면 true(선택 멤버십 변경).
   */
  private detachDisprovenSelections(): boolean {
    if (this.selectedRows.size === 0) {
      return false;
    }
    const rowIndex = this.currentRowIndex();
    let detached = false;
    for (const [rowId, capturedRow] of this.selectedRows) {
      const capturedParentId =
        capturedRow instanceof TableChildRow ? capturedRow.getParentRowId() : null;
      const currentRow = rowIndex.get(rowId);
      if (currentRow) {
        const currentParentId =
          currentRow instanceof TableChildRow ? currentRow.getParentRowId() : null;
        if (currentParentId !== capturedParentId && this.selectedRows.delete(rowId)) {
          devWarn(
            `[c6] Selected row "${rowId}" was re-declared under a different parent ` +
              `("${capturedParentId ?? "(top-level)"}" -> "${currentParentId ?? "(top-level)"}"); ` +
              `reparenting a selected row is not supported — its selection has been detached (RFC-data-table).`,
          );
          detached = true;
        }
        continue;
      }
      if (
        capturedParentId !== null &&
        rowIndex.has(capturedParentId) &&
        this.selectedRows.delete(rowId)
      ) {
        detached = true;
      }
    }
    return detached;
  }

  /**
   * rawRows 변경은 반드시 이 경로로 커밋한다 — canShow 필터가 스냅샷 경계 이 한 곳에서만
   * 실행되므로, `snapshot.rows === visible(rawRows)` 불변식이 사이트별 관례가 아니라
   * 구조로 지켜진다. 반증된 선택 수거(사라진 하위 행 + 소속 변경 가드)도 같은 이유로 여기서만 한다.
   *
   * 선택된 행 **객체**는 여기서 손대지 않는다 — 읽을 때 해석하므로 보관본을 따라다니며 고칠
   * 필요가 없다(PIT-stored-object-identity-drift).
   */
  private commitRows(
    nextRawRows: readonly Row[],
    patch: Partial<DataTableSourceSnapshot<Row>> = {},
  ): void {
    this.rawRows = nextRawRows;
    const detached = this.detachDisprovenSelections();
    if (detached) {
      this.selectionIds = new Set(this.selectedRows.keys());
    }
    if (patch.total !== undefined && !patch.append && this.snapshot.append?.total != null) {
      patch.append = { ...this.snapshot.append, total: patch.total };
    }
    this.setSnapshot({
      ...this.snapshot,
      ...patch,
      // 수거는 patch가 계산된 뒤에 났으므로 patch.selectedIds보다 뒤에 얹는다.
      ...(detached ? { selectedIds: this.selectionIds } : {}),
      rows: this.rawRows.filter((row) => row.canShow()),
    });
    if (detached) {
      this.onSelectionChange?.(this.getSelectedRows(), this.getSelectedChildRows());
    }
  }

  // 로드/캐시 내부: 요청 경합(serial)·prefetch·LRU·generation 무효화
  protected replaceLoadedPage(response: DataTablePageResp<Row>): void {
    if (this.disposed) {
      return;
    }

    // 원본을 보관하고 visible만 스냅샷에 싣는다. 캐시는 원본 응답을 저장하므로
    // 캐시 히트 경로(goToPage → replaceLoadedPage)도 표시 시점에 최신 필터를 통과한다.
    this.commitRows(Array.from(response.rows), {
      loadState: "loaded",
      errorMessage: null,
      page: response.page,
      size: response.size,
      total: response.total,
      selectedIds: this.selectionIds,
    });
    const scope = this.buildCacheScope(response.size);
    this.displayedPage = scope === null ? null : { scope, page: response.page };
  }

  protected onPage(
    req: DataTablePageReq,
    options?: DataTablePageOptions,
  ): Promise<DataTablePageResp<Row>> {
    void req;
    void options;
    throw new Error("Implement onPage for numbered pages or onAppend for cursor loading.");
  }

  /** 기본 어댑터는 onPage를 순차 조회한다. 커서 API는 이 hook만 override한다. */
  protected async onAppend(req: DataTableAppendReq): Promise<DataTableAppendResp<Row>> {
    const page = req.cursor === null ? 1 : Number(req.cursor);
    const response = await this.onPage({ page, size: req.size }, { signal: req.signal });
    return {
      rows: response.rows,
      total: response.total,
      nextCursor: response.page * response.size < response.total ? String(page + 1) : null,
    };
  }

  /**
   * prefetch/캐시 키의 criteria 부분. 기본은 `null`이며, 이 경우 캐시·prefetch를 하지 않는다.
   * search/filter 같은 criteria를 가진 source가 override해 그 서명을 반환하면 활성화된다.
   * criteria가 바뀌면 서명이 달라져 캐시가 자연히 무효화된다.
   */
  protected cacheSignature(): string | null {
    return null;
  }

  private buildCacheScope(size: number): string | null {
    const signature = this.cacheSignature();
    if (signature === null) {
      return null;
    }
    return `${signature}|s=${size}`;
  }

  private buildCacheKey(page: number, size: number): string | null {
    const scope = this.buildCacheScope(size);
    return scope === null ? null : toCacheKey(scope, page);
  }

  /**
   * page 모드의 요청 경로. 같은 캐시 키의 요청이 진행 중이면 그 요청을 돌려준다.
   * 그래서 진행 중인 prefetch와 같은 page를 열어도 요청이 한 번만 나간다.
   */
  private requestPage(page: number, size: number): PendingPage<Row> {
    const scope = this.buildCacheScope(size);
    const cacheKey = scope === null ? null : toCacheKey(scope, page);
    const pending = cacheKey === null ? undefined : this.pendingPages.get(cacheKey);
    if (pending !== undefined) {
      return pending;
    }

    const controller = new AbortController();
    const request: PendingPage<Row> = {
      controller,
      cacheKey,
      scope,
      prefetch: false,
      // async로 감싸 onPage의 동기 throw도 reject로 받는다.
      promise: (async () => this.onPage({ page, size }, { signal: controller.signal }))(),
    };
    if (cacheKey !== null) {
      this.pendingPages.set(cacheKey, request);
    }
    // 끝난 요청의 등록을 지운다. 결과와 실패는 이 요청을 기다리는 쪽이 처리한다.
    const forget = (): void => this.forgetPendingPage(request);
    request.promise.then(forget, forget);
    return request;
  }

  private forgetPendingPage(request: PendingPage<Row>): void {
    if (request.cacheKey !== null && this.pendingPages.get(request.cacheKey) === request) {
      this.pendingPages.delete(request.cacheKey);
    }
  }

  private dropPendingPage(request: PendingPage<Row>): void {
    // 등록을 먼저 지운다. abort 리스너가 같은 page를 다시 열어도 취소한 요청에 합류하지 않는다.
    this.forgetPendingPage(request);
    request.controller.abort();
  }

  // 화면용 요청을 바꾼다. 앞의 요청이 같은 요청(같은 키에 합류)이거나 prefetch가 기다리는
  // 요청이면 취소하지 않는다.
  private replaceDisplayRequest(next: PendingPage<Row> | null): void {
    const previous = this.displayRequest;
    this.displayRequest = next;
    if (previous !== null && previous !== next && !previous.prefetch) {
      this.dropPendingPage(previous);
    }
  }

  // 조건·정렬·size가 달라진 진행 중 요청은 어느 화면에도 쓰이지 않는다.
  private abortPendingOutsideScope(scope: string | null): void {
    for (const request of Array.from(this.pendingPages.values())) {
      if (request.scope !== scope) {
        this.dropPendingPage(request);
      }
    }
  }

  // from: 사용자가 goToPage로 넘겨 온 경우 직전에 표시한 page. 그 밖의 로드는 null이다.
  private schedulePrefetch(from: DisplayedPage | null, page: number, size: number): void {
    if (this.disposed) {
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.snapshot.total / size));
    if (this.prefetchMode !== "paging") {
      if (page > 1) {
        void this.prefetchPage(page - 1, size);
      }
      if (page < totalPages) {
        void this.prefetchPage(page + 1, size);
      }
      return;
    }
    // 조건을 바꿔 받은 page 뒤에는 다음 page로 간다는 근거가 없다.
    if (from === null || from.scope !== this.buildCacheScope(size)) {
      return;
    }
    // 같은 page를 다시 연 경우 target은 방금 캐시한 그 page다. prefetchPage가 건너뛴다.
    const target = page + Math.sign(page - from.page);
    if (target >= 1 && target <= totalPages) {
      void this.prefetchPage(target, size);
    }
  }

  private async prefetchPage(page: number, size: number): Promise<void> {
    const cacheKey = this.buildCacheKey(page, size);
    if (cacheKey === null || this.pageCache.has(cacheKey)) {
      return;
    }

    const generationAtStart = this.cacheGeneration;
    const request = this.requestPage(page, size);
    request.prefetch = true;
    try {
      const response = await request.promise;
      // 도착 시점에 캐시가 비워지지 않았고(criteria/데이터 불변) size·키가 그대로일 때만 캐시한다.
      // source가 취소한 요청은 전송이 값을 돌려줘도 캐시하지 않는다.
      const stillFresh =
        !this.disposed &&
        !request.controller.signal.aborted &&
        this.cacheGeneration === generationAtStart &&
        this.buildCacheKey(page, size) === cacheKey &&
        size === this.snapshot.size;
      if (!stillFresh) {
        return;
      }
      this.cachePage(cacheKey, response);
    } catch {
      // prefetch 실패는 화면에 표시하지 않는다. 다음 탐색에서 정상 경로로 다시 받는다.
    }
  }

  private clearPageCache(): void {
    this.pageCache.clear();
    // generation을 올려, 비우기 이전에 시작된 load/prefetch가 도착해도 캐시에 쓰지 않게 한다.
    this.cacheGeneration += 1;
    // 비우기 이전에 시작한 요청은 무효화 이전 응답을 준다. prefetch는 취소한다. 화면용 요청은
    // 지금 표시할 응답이라 취소하지 않는다. 등록만 지워 뒤의 요청이 합류하지 않게 한다.
    for (const request of this.pendingPages.values()) {
      if (request === this.displayRequest) {
        // 기다리던 prefetch는 이 응답을 캐시하지 않는다. 화면이 떠나면 취소한다.
        request.prefetch = false;
      } else {
        request.controller.abort();
      }
    }
    this.pendingPages.clear();
  }

  private cachePage(cacheKey: string, response: DataTablePageResp<Row>): void {
    // 최근 사용 항목을 뒤로 보내고, 상한을 넘으면 가장 오래된 항목부터 버린다(LRU 근사).
    this.pageCache.delete(cacheKey);
    this.pageCache.set(cacheKey, response);
    while (this.pageCache.size > maxCachedPages) {
      const oldestKey = this.pageCache.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      this.pageCache.delete(oldestKey);
    }
  }

  // navigated: goToPage로 온 로드다. refresh·size 변경은 false다.
  private async loadPage(page: number, size: number, navigated: boolean): Promise<void> {
    // 최신 요청만 snapshot을 갱신하도록 요청 번호를 증가시킨다.
    this.disposed = false;
    const requestId = ++this.requestSerial;
    const generationAtStart = this.cacheGeneration;
    this.abortPendingOutsideScope(this.buildCacheScope(size));
    this.setSnapshot({
      ...this.snapshot,
      loadState: "loading",
      errorMessage: null,
      page,
      size,
    });
    // 통지를 받은 구독자가 더 새 로드를 시작했으면 이 로드는 이미 버려졌다. 요청을 보내지 않는다.
    if (this.disposed || requestId !== this.requestSerial) {
      return;
    }
    const request = this.requestPage(page, size);
    // onPage의 동기 구간이 더 새 로드를 시작한 경우도 같다. 방금 보낸 요청은 화면에 쓰이지 않는다.
    if (this.disposed || requestId !== this.requestSerial) {
      if (request !== this.displayRequest && !request.prefetch) {
        this.dropPendingPage(request);
      }
      return;
    }
    this.replaceDisplayRequest(request);

    try {
      const response = await request.promise;
      // dispose 되었거나 더 늦은 요청이 시작됐으면 응답을 버린다.
      if (this.disposed || requestId !== this.requestSerial) {
        return;
      }

      // 스냅샷 구성(원본 보관 + canShow 필터)은 replaceLoadedPage 한 곳으로 통일한다.
      const from = navigated ? this.displayedPage : null;
      this.replaceLoadedPage(response);

      // 방금 받은 페이지를 캐시하고 prefetchMode에 따라 미리 받아 다음 탐색을 즉시화한다.
      // 단, 로드 도중 캐시가 비워졌다면(mutation/refresh) 무효화 이전 응답일 수 있어 캐시하지 않는다.
      // 요청 중에 조건만 바뀌고 다시 받지 않은 응답도 캐시하지 않는다. 그 응답은 지금 조건의 page가
      // 아니다. 지금 조건의 키로 캐시하면 다음 이동이 다른 조건의 행을 요청 없이 표시한다.
      const cacheKey = this.buildCacheKey(response.page, response.size);
      const scopeUnchanged = request.scope === this.buildCacheScope(size);
      if (!scopeUnchanged) {
        // 표시한 행이 지금 조건의 page가 아니다. 다음 이동의 방향을 이 page에서 재지 않는다.
        this.displayedPage = null;
      }
      if (cacheKey !== null && scopeUnchanged && this.cacheGeneration === generationAtStart) {
        this.cachePage(cacheKey, response);
        this.schedulePrefetch(from, response.page, response.size);
      }
    } catch (error) {
      // 실패도 최신 요청에 대해서만 화면에 반영한다. 취소된 요청의 reject는 여기서 버려진다.
      if (this.disposed || requestId !== this.requestSerial) {
        return;
      }

      this.setSnapshot({
        ...this.snapshot,
        loadState: "failed",
        errorMessage: this.resolveErrorMessage(error),
      });
    } finally {
      if (this.displayRequest === request) {
        this.displayRequest = null;
      }
    }
  }

  private setSnapshot(snapshot: DataTableSourceSnapshot<Row>): void {
    this.snapshot = snapshot;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * 이 행의 선택 체크박스를 지금 조작할 수 있는가 = 이미 선택됨(해제는 항상 가능 — 상한형
 * 규칙이 자기 자신을 잠그지 않는다) 또는 새로 선택 가능(canSelectRow).
 * 뷰의 disabled 렌더와 전체선택 대상(resolveSelectAllState)이 이 한 판정을 공유한다.
 */
export function canToggleRowSelection<Row extends TableRow>(
  row: TableRowBase,
  selectedIds: ReadonlySet<string>,
  source: DataTableSource<Row>,
): boolean {
  return selectedIds.has(row.getRowId()) || source.canSelectRow(row);
}

/**
 * 전체선택(표 헤더/그리드 바)·부모 행의 자식 집계 토글의 대상·checked·indeterminate 계산.
 * 대상은 조작 가능한 행(canToggleRowSelection)이다 — 이미 선택된 행을 포함해야 상한 도달 시
 * 전체선택이 indeterminate에 머물지 않고 checked에 도달한다(다시 누르면 해제). DataTable
 * 헤더·부모 행 체크박스와 DataGridView 바가 이 계산을 공유해 드리프트를 막는다.
 */
export function resolveSelectAllState<Row extends TableRow, Target extends TableRowBase>(
  rows: readonly Target[],
  selectedIds: ReadonlySet<string>,
  source: DataTableSource<Row>,
): { selectableRows: readonly Target[]; allSelected: boolean; someSelected: boolean } {
  const selectableRows = rows.filter((row) => canToggleRowSelection(row, selectedIds, source));
  return {
    selectableRows,
    allSelected:
      selectableRows.length > 0 && selectableRows.every((row) => selectedIds.has(row.getRowId())),
    someSelected: selectableRows.some((row) => selectedIds.has(row.getRowId())),
  };
}

// generic c6 기본 문구는 영어다. 로케일 문구가 필요한 소비처는 resolveErrorMessage 옵션으로 주입한다.
function resolveDefaultErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to load data.";
}
